import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../lib/cn';

export interface SuccessAnimationProps {
  titel: string;
  beschreibung?: ReactNode;
  /** Kantenlänge des Zeichens in Pixeln. */
  groesse?: number;
  /** Aktionen unter der Meldung, z. B. „Abrechnung öffnen“. */
  aktion?: ReactNode;
  /** Wird nach Ablauf der Animation ausgelöst. */
  onFertig?: () => void;
  ton?: 'erfolg' | 'akzent';
  className?: string;
}

/**
 * Abschluss-Zeichen für erfolgreiche Vorgänge (Abrechnung abgeschlossen,
 * Zahlungseingang erfasst): Ring und Haken werden über `pathLength` gezeichnet.
 * Bei reduzierter Bewegung erscheint das Zeichen ohne Animation.
 */
export function SuccessAnimation({
  titel,
  beschreibung,
  groesse = 96,
  aktion,
  onFertig,
  ton = 'erfolg',
  className,
}: SuccessAnimationProps) {
  const reduziert = useReducedMotion();
  const farbe = ton === 'erfolg' ? 'text-success' : 'text-accent';

  // Abschluss-Callback in einer Ref halten, damit der Timer NICHT bei jedem Re-Render
  // des Eltern-Elements neu gestartet wird (sonst kann ein laufender Countdown den
  // Übergang endlos verhindern). Der Timer läuft genau einmal.
  const onFertigRef = useRef(onFertig);
  onFertigRef.current = onFertig;

  useEffect(() => {
    const dauer = reduziert ? 0 : 1100;
    const timer = window.setTimeout(() => onFertigRef.current?.(), dauer);
    return () => window.clearTimeout(timer);
  }, [reduziert]);

  const ringUebergang = reduziert
    ? { duration: 0 }
    : { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] as const };
  const hakenUebergang = reduziert
    ? { duration: 0 }
    : { duration: 0.35, delay: 0.4, ease: [0.2, 0.8, 0.2, 1] as const };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex flex-col items-center text-center', className)}
    >
      <div className="relative" style={{ width: groesse, height: groesse }}>
        {/* Weicher Puls hinter dem Zeichen */}
        {!reduziert && (
          <motion.span
            initial={{ opacity: 0.5, scale: 0.6 }}
            animate={{ opacity: 0, scale: 1.35 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className={cn('absolute inset-0 rounded-pill bg-current opacity-20', farbe)}
            aria-hidden="true"
          />
        )}

        <svg
          viewBox="0 0 100 100"
          width={groesse}
          height={groesse}
          fill="none"
          className={cn('relative', farbe)}
          aria-hidden="true"
        >
          <circle cx="50" cy="50" r="44" stroke="currentColor" strokeWidth="3" opacity="0.16" />
          <motion.circle
            cx="50"
            cy="50"
            r="44"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            initial={{ pathLength: 0, rotate: -90 }}
            animate={{ pathLength: 1, rotate: -90 }}
            style={{ transformOrigin: '50% 50%' }}
            transition={ringUebergang}
          />
          <motion.path
            d="M31 51.5 L44.5 65 L70 36"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={hakenUebergang}
          />
        </svg>
      </div>

      <motion.p
        initial={reduziert ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: reduziert ? 0 : 0.55 }}
        className="mt-4 text-lg font-semibold text-fg"
      >
        {titel}
      </motion.p>

      {beschreibung && (
        <motion.div
          initial={reduziert ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: reduziert ? 0 : 0.65 }}
          className="mt-1.5 max-w-md text-sm leading-relaxed text-fg-muted"
        >
          {beschreibung}
        </motion.div>
      )}

      {aktion && <div className="mt-5 flex flex-wrap justify-center gap-2">{aktion}</div>}
    </div>
  );
}
